// src/App.js
import React from 'react';
import './App.css';
import { auth } from './firebase';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// --- React Bootstrap Imports ---
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Button from 'react-bootstrap/Button';
// import NavDropdown from 'react-bootstrap/NavDropdown'; // Keep if you plan to use it

// --- Component Imports ---
// ... (your component imports remain the same) ...
import FamilyList from './components/FamilyList';
import FamilyDetail from './components/FamilyDetail';
import FamilyGrid from './components/FamilyGrid';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import GroupsPage from './components/GroupsPage';
import AddMemberPage from './components/AddMemberPage';
import EditMemberPage from './components/EditMemberPage';
import Home from './components/Home';
import Footer from './components/Footer';
import UserManagement from './components/UserManagement';
import ForgotPassword from './components/ForgotPassword';
import AddFamilyPage from './components/AddFamilyPage';
import EditFamilyPage from './components/EditFamilyPage';
import UserProfilePage from './components/UserProfilePage';
import CreateAccountPage from './components/CreateAccountPage';
import RegisterOrganizationPage from './components/RegisterOrganization';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import ProtectedRoute from './components/ProtectedRoute';
import AddGroupPage from './components/AddGroupPage';
import MapPage from './components/MapPage';
import DocumentsPage from './components/DocumentsPage';
import DataAuditPage from './components/DataAuditPage';
import OrgManagementPage from './components/OrgManagementPage';
import HelpPage from './components/HelpPage';
import AssignMemberPhotos from './components/AssignMemberPhotos';
import AssignSingleMemberPhotoPage from './components/AssignSingleMemberPhotoPage';
import MemberDetail from './components/MemberDetail';

// Font Awesome Icons - CORRECTED IMPORTS
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faQuestionCircle,
    faAddressCard, // Regular or Solid
    faFileAlt      // Regular or Solid
} from '@fortawesome/free-regular-svg-icons'; // Icons that are often preferred in their 'regular' style

import {
    faUserCircle,
    faSignOutAlt,
    faTachometerAlt, // Moved to Solid
    faUsers,
    faMapMarkedAlt,
    faTasks
} from '@fortawesome/free-solid-svg-icons'; // Icons that are often used in their 'solid' style


// Inner component that has access to the Auth Context
function AppContent() {
  const { currentUser, loadingAuth, isAdmin } = useAuth();

  if (loadingAuth) {
    return ( <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}> <div>Loading Application...</div> </div> );
  }

  return (
      <div className="App d-flex flex-column min-vh-100">
        <header>
            <Navbar bg="dark" variant="dark" expand="lg" collapseOnSelect sticky="top">
                <Container fluid>
                    <Navbar.Brand as={RouterLink} to={currentUser ? "/dashboard" : "/"}>
                        <img src="/assets/logoCA24.png" width="182" height="30" alt="Logo" className="d-inline-block align-top"/>
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="responsive-navbar-nav" />
                    <Navbar.Collapse id="responsive-navbar-nav">
                        {/* Left Nav Items */}
                        <Nav className="me-auto">
                            {currentUser && (
                                <>
                                    <Nav.Link as={RouterLink} to="/dashboard" className="py-2 py-lg-1 px-lg-2">
                                        <FontAwesomeIcon icon={faTachometerAlt} className="me-2 d-lg-none" />Dashboard
                                    </Nav.Link>
                                    <Nav.Link as={RouterLink} to="/families" className="py-2 py-lg-1 px-lg-2">
                                        <FontAwesomeIcon icon={faAddressCard} className="me-2 d-lg-none" />Families
                                    </Nav.Link>
                                    <Nav.Link as={RouterLink} to="/groups" className="py-2 py-lg-1 px-lg-2">
                                        <FontAwesomeIcon icon={faUsers} className="me-2 d-lg-none" />Groups
                                    </Nav.Link>
                                    <Nav.Link as={RouterLink} to="/map" className="py-2 py-lg-1 px-lg-2">
                                        <FontAwesomeIcon icon={faMapMarkedAlt} className="me-2 d-lg-none" />Map
                                    </Nav.Link>
                                    {isAdmin && (
                                        <Nav.Link as={RouterLink} to="/admin/users" className="py-2 py-lg-1 px-lg-2">
                                            <FontAwesomeIcon icon={faTasks} className="me-2 d-lg-none" />User Management
                                        </Nav.Link>
                                    )}
                                </>
                            )}
                        </Nav>
                        {/* Right Nav Items */}
                        <Nav className="align-items-lg-center">
                            {currentUser ? (
                            <>
                                <Nav.Link as={RouterLink} to="/help" title="Help" className="py-2 py-lg-1 px-lg-2">
                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                    <span className="d-lg-none ms-2">Help</span>
                                </Nav.Link>
                                <Nav.Link as={RouterLink} to="/profile" title="My Profile" className="py-2 py-lg-1 px-lg-2">
                                    <FontAwesomeIcon icon={faUserCircle} size="lg" />
                                    <span className="d-lg-none ms-2">My Profile</span>
                                </Nav.Link>
                                <div className="d-grid d-lg-inline-block mt-2 mt-lg-0 ms-lg-2 px-2 px-lg-0">
                                    <Button variant="primary" size="sm" onClick={() => auth.signOut()} className="w-100">
                                       <FontAwesomeIcon icon={faSignOutAlt} className="me-2 d-lg-none" />
                                       Logout
                                    </Button>
                                </div>
                            </>
                            ) : (
                                null
                            )}
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
        </header>

        {/* Main Content Area - Routing (Unchanged) */}
        <main role="main" className="container-fluid mt-3 flex-grow-1">
          <Routes>
            {/* ... (routes remain the same) ... */}
             {/* --- Public Routes --- */}
             <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/create-account" element={<CreateAccountPage />} />
            <Route path="/create-account/:orgId" element={<CreateAccountPage />} />
            <Route path="/register-organization" element={<RegisterOrganizationPage />} />
            <Route path="/" element={currentUser ? <Navigate to="/dashboard" replace /> : <Home />} />
            <Route path="/login" element={!currentUser ? <Login /> : <Navigate to="/dashboard" replace />} />
            <Route path="/forgot-password" element={!currentUser ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} />

            {/* --- Protected Routes --- */}
            <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
            <Route path="/map" element={<ProtectedRoute element={<MapPage />} />} />
            <Route path="/documents" element={<ProtectedRoute element={<DocumentsPage />} />} />

            {/* --- Families Routes (Corrected Order & Syntax) --- */}
            <Route path="/families" element={<ProtectedRoute element={<FamilyList />} />} />
            <Route path="/families/add" element={<ProtectedRoute element={<AddFamilyPage />} />} />
            <Route path="/families/grid" element={<ProtectedRoute element={<FamilyGrid />} />} />
            <Route path="/families/edit/:familyId" element={<ProtectedRoute element={<EditFamilyPage />} />} />
            <Route path="/families/:familyId/assign-photos/all" element={<ProtectedRoute element={<AssignMemberPhotos />} />} />
            <Route path="/families/:familyId/assign-photos" element={<ProtectedRoute element={<AssignSingleMemberPhotoPage />} />} />
            <Route path="/families/:familyId/member/:memberId" element={<ProtectedRoute element={<MemberDetail />} />} />
            <Route path="/families/:familyId" element={<ProtectedRoute element={<FamilyDetail />} />} />

            {/* --- Other Routes --- */}
            <Route path="/groups" element={<ProtectedRoute element={<GroupsPage />} />} />
            <Route path="/groups/add" element={<ProtectedRoute element={<AddGroupPage />} />} />
            <Route path="/members/add" element={<ProtectedRoute element={<AddMemberPage />} />} />
            <Route path="/members/edit/:memberId" element={<ProtectedRoute element={<EditMemberPage />} />} />
            <Route path="/admin/users" element={<ProtectedRoute element={<UserManagement />} />} />
            <Route path="/admin/data-audit" element={<ProtectedRoute element={<DataAuditPage />} />} />
            <Route path="/admin/org" element={<ProtectedRoute element={<OrgManagementPage />} />} />
            <Route path="/help" element={<ProtectedRoute element={<HelpPage />} />} />

            {/* --- Profile Route (Login required, verification not) --- */}
            <Route
                path="/profile"
                element={currentUser ? <UserProfilePage /> : <Navigate to="/" replace />}
            />

            {/* --- Fallback Routes --- */}
            {currentUser && <Route path="*" element={<Navigate to="/dashboard" replace />} />}
            {!currentUser && <Route path="*" element={ <div className='text-center mt-5'> <h2>404 Not Found</h2> <p>The page you requested could not be found.</p> <RouterLink to="/">Go to Home</RouterLink> </div> } />}
          </Routes>
        </main>

        <Footer />
      </div>
  );
}

// Main App component (Unchanged)
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;